import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Wind, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import ProgressBar from '../components/ProgressBar';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';

const iconMap = {
  'Oven': { icon: Wind, color: 'text-blue-400' },
  'Gefrituurd': { icon: Flame, color: 'text-orange-400' }
};

const defaultIcon = { icon: Flame, color: 'text-orange-400' };

export const PreparationMethod = ({ order, setOrder }) => {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const productId = order.product?.id;

  const fetchMethods = useCallback(async () => {
    if (!productId) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('product_preparation_methods')
      .select('preparation_method_id, preparation_methods(*)')
      .eq('product_id', productId);

    if (error) {
      setLoading(false);
      return;
    }

    const enriched = data.map(ppm => {
      const m = ppm.preparation_methods;
      const meta = iconMap[m.name] || defaultIcon;
      return {
        id: m.id,
        name: m.name,
        description: m.description,
        icon: meta.icon,
        color: meta.color
      };
    });

    setMethods(enriched);
    setLoading(false);
  }, [productId]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const handleSelect = (method) => {
    setOrder({ ...order, preparation: method });
    navigate('/flavor');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <ProgressBar currentStep={2} totalSteps={6} />
      <PageHeader 
        title="Bereidingswijze"
        subtitle="Hoe wil je je kip bereid hebben?"
      />

      <div className="container max-w-4xl mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {methods.map((method, index) => {
            const IconComponent = method.icon;
            return (
              <motion.div
                key={method.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.15, duration: 0.4 }}
              >
                <Card
                  onClick={() => handleSelect(method)}
                  data-testid={`preparation-${method.name.toLowerCase()}`}
                  className={`product-card p-10 cursor-pointer border-2 bg-card hover:border-primary touch-feedback min-h-[280px] flex flex-col items-center justify-center text-center ${
                    order.preparation?.id === method.id ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center mb-6">
                    <IconComponent className={`w-16 h-16 ${method.color}`} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-3xl font-bold text-foreground mb-3">{method.name}</h3>
                  <p className="text-lg text-muted-foreground">{method.description}</p>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PreparationMethod;
